import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  EVDialog, EVDialogContent, EVDialogActions, EVDialogContentText,
  EVIconButton, EVClear, EVBox, EVButton, EVCheckbox,
} from '@eagleview/ev-comp-library';
import { FEATURE_ENTITLEMENTS, ASSESS_RAW_IMAGERY_ENTITLEMENTS } from 'layout/entitleUser/EntitleUser.constants';
import {
  UNSET_ANOMALY_ID, ANOMALY_DECISIONS,
  TESTSQUARE_ZOOM_LEVEL_ON_SELECTION,
} from 'layout/adjuster/Adjuster.constants';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isEntitled } from 'utils/auth.utils';
import * as action from 'layout/adjuster/Adjuster.actions';
import ErrorBoundary from 'components/ErrorBoundary';
import { get, union } from 'lodash';
import TestSquareWireFrameMapViewer from './TestSquareWireFrameMapViewer/TestSquareWireFrameMapViewer';
import useStyles from './TestsquareAnomalyDecisionsPanel.styles';
import AnomalyCardItem from './AnomalyCardItem';
import FacetImagesDialog from '../facet-images-dialog/FacetImagesDialog';
import FacetImagesRawImageryDialog from '../facet-images-raw-imagery-dialog/FacetImagesRawImageryDialog';
import TestSquareAnomalyNotesPanel from './TestSquareAnomalyNotesPanel';
import { annotationDescPanelConstants, isCompleteVisibleH, isCompleteVisibleV } from '../annotation-description';
import {
  validateRefs,
  findTargetAnomaly,
  validateAnomalyGeometry,
  extractCoordinates,
  calculateAnomalyZoomLevel,
  calculateSmartPosition,
  clampPositionToContainer,
  createEaseToConfig,
  createPanelPosition,
} from './anomalyDescriptionUtils';
import * as utils from '../Adjuster.utils';

const TestsquareAnomalyDecisionsPanel = ({
  open, onClose, testSquareData, anomalies, showRemovedAnomalies, toggleRemovedAnomalies,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const facetPolygons = useSelector((state) => state.adjusterReducer.facetPolygons);
  const testSquare = useSelector((state) => state.adjusterReducer.testSquareData);
  const entitlements = useSelector((state) => state.entitleUserReducer.entitlements);
  const orderID = useSelector((state) => state.adjusterReducer.orderId);
  const isFacetRawImageryWorkflowEnabled = useSelector((state) => get(state.entitleUserReducer.featureFlags, ASSESS_RAW_IMAGERY_ENTITLEMENTS, false));
  const facets = useSelector((state) => state.adjusterReducer.facets);
  const roofId = useSelector((state) => state.adjusterReducer.roofId);
  const selectedStructureId = useSelector((state) => state.adjusterReducer.selectedStructureId);
  const anomalySnapshots = useSelector((state) => state.adjusterReducer.anomalySnapshots || {});
  // Action dispatchers for individual anomaly cards
  const updateAnomalyType = (payload) => dispatch(action.updateAnomalyTypeAction(payload));
  const saveAnomalyDecision = (payload) => dispatch(action.saveAnomalyDecisionAction(payload));
  const deleteUserAnomaly = (payload) => dispatch(action.deleteUserAnomalyAction(payload));
  const setAnomalyDecision = (payload) => dispatch(action.setAnomalyDecisionAction(payload));
  const toggleAnomalyIncludeInReport = (payload) => dispatch(action.toggleAnomalyIncludeInReportAction(payload));
  const saveFacetReportInclusion = (payload) => dispatch(action.saveFacetReviewDecisionAction(payload));
  const fetchAnomalyNoteDetails = (payload) => dispatch(action.getAnomalyNotesAction(payload));
  const fetchAnomalySnapshots = (payload) => dispatch(action.fetchAnomalySnapshotsAction(payload));
  const clearAnomalyNotes = () => dispatch(action.clearAnomalyNotesAction());
  const fetchAnomalyImages = (payload) => dispatch(action.fetchAnomalyImagesAction(payload));
  const [facetImageGalleryDialogOpen, setfacetImageGalleryDialogOpen] = useState(false);
  const [isAnomalyImages, setIsAnomalyImages] = useState(false);
  const [hoveredAnomalyId, setHoveredAnomalyId] = useState('');
  const [newlyAddedAnomalyIdstate, setNewlyAddedAnomalyIds] = useState([]);
  const lastMousePositionRef = useRef({ x: 0, y: 0 });

  // ref's
  const wireframeMapViewerRef = useRef();
  const wrapperRef = useRef();
  const previousAnomaliesRef = useRef([]);
  // State for anomaly description panel
  const [showAnomalyDescription, setShowAnomalyDescription] = useState(false);
  const [selectedAnomalyId, setSelectedAnomalyId] = useState('');
  const [anomalyDescriptionPosition, setAnomalyDescriptionPosition] = useState({ top: 0, left: 0 });

  // Ref for the scrollable anomaly cards container
  const anomalyCardsContainerRef = useRef(null);
  // Ref for the wireframe map viewer
  // const wireframeMapViewerRef = useRef(null);

  // State for delete user anomaly confirmation dialog
  const [deleteUserAnomalyDialogOpen, setDeleteUserAnomalyDialogOpen] = useState(false);
  const [deleteAnomalyId, setDeleteAnomalyId] = useState(UNSET_ANOMALY_ID);

  // Get only anomalies inside the test square
  const getTestSquareAnomalies = () => {
    if (!anomalies || !anomalies[0] || !anomalies[0].data || !testSquare || !testSquare.anomalies) return [];
    return anomalies[0].data.filter((anomaly) => testSquare.anomalies.includes(anomaly.properties.anomalyId));
  };
  const testSquareAnomalies = getTestSquareAnomalies();

  // Function to scroll to a specific anomaly card
  const scrollToAnomalyCard = (anomalyId) => {
    if (!anomalyCardsContainerRef.current || !anomalyId) return;

    const container = anomalyCardsContainerRef.current;
    const anomalyIndex = testSquareAnomalies.findIndex(
      (anomaly) => anomaly.properties.anomalyId === anomalyId,
    );

    if (anomalyIndex === -1) return;

    // Get the actual anomaly card element
    const anomalyCards = container.querySelectorAll('[data-anomaly-id]');
    const targetCard = Array.from(anomalyCards).find(
      (card) => card.getAttribute('data-anomaly-id') === anomalyId,
    );

    if (!targetCard) {
      // If card not found, try again after a short delay (in case of rendering timing)
      setTimeout(() => scrollToAnomalyCard(anomalyId), 100);
      return;
    }

    // Calculate the position of the anomaly card relative to the container
    const containerRect = container.getBoundingClientRect();
    const cardRect = targetCard.getBoundingClientRect();

    // Calculate the scroll position needed to bring the card into view
    const cardTop = cardRect.top - containerRect.top + container.scrollTop;
    const cardBottom = cardTop + cardRect.height;
    const containerHeight = container.clientHeight;
    const currentScrollTop = container.scrollTop;

    // Check if the anomaly is already visible
    const isVisible = cardTop >= currentScrollTop && cardBottom <= currentScrollTop + containerHeight;

    if (!isVisible) {
      // Scroll to the anomaly card with smooth animation
      // If card is below viewport, scroll to show it at the bottom
      // If card is above viewport, scroll to show it at the top
      let targetScrollTop;
      if (cardTop < currentScrollTop) {
        // Card is above viewport - scroll to show it at the top with a small offset
        targetScrollTop = Math.max(0, cardTop - 20);
      } else {
        // Card is below viewport - scroll to show it at the bottom with a small offset
        targetScrollTop = cardBottom - containerHeight + 20;
      }

      // console.log(`Scrolling to anomaly ${anomalyId} at position ${targetScrollTop}`);

      container.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth',
      });
    } else {
      // console.log(`Anomaly ${anomalyId} is already visible`);
    }
  };

  // Initialize with first anomaly by default
  const [selectedAnomalyForDialog, setSelectedAnomalyForDialog] = useState(
    testSquareAnomalies.length > 0 ? testSquareAnomalies[0] : null,
  );

  // Lookup function to find the full facet info for an anomaly
  const getFacetInfoForAnomaly = (anomaly) => {
    if (!facets || !anomaly) return null;
    return facets.find((facet) => facet.facetId === anomaly.properties.facetId);
  };

  // Memoized selected facet info for the dialog
  const selectedFacetForDialog = selectedAnomalyForDialog ? getFacetInfoForAnomaly(selectedAnomalyForDialog) : null;

  // Helper function to get anomaly information for the currently selected anomaly in description panel
  const getSelectedAnomalyInfo = () => {
    if (!selectedAnomalyId || !testSquareAnomalies) return null;
    return testSquareAnomalies.find((anomaly) => anomaly.properties.anomalyId === selectedAnomalyId);
  };

  // Helper function to get facet information for the currently selected anomaly
  const getSelectedAnomalyFacetInfo = () => {
    const selectedAnomaly = getSelectedAnomalyInfo();
    if (!selectedAnomaly) return null;
    return getFacetInfoForAnomaly(selectedAnomaly);
  };

  // Helper function to check if selected anomaly is user added
  const isSelectedAnomalyUserAdded = () => {
    const selectedAnomaly = getSelectedAnomalyInfo();
    return selectedAnomaly ? get(selectedAnomaly, 'properties.isUserAdded', false) : false;
  };

  // Helper function to check if selected anomaly is confirmed
  const isSelectedAnomalyConfirmed = () => {
    const selectedAnomaly = getSelectedAnomalyInfo();
    return selectedAnomaly ? get(selectedAnomaly, 'properties.status', '') === ANOMALY_DECISIONS.CONFIRMED : false;
  };

  const openAnomalyImagesDialog = (anomaly) => {
    dispatch(action.clearAnomalyImagesAction());
    const centroid = utils.getCentroidForGeometry(anomaly.geometries[0]);
    const latlon = {
      lat: centroid.geometry.coordinates[1],
      lon: centroid.geometry.coordinates[0],
    };
    const facetInfo = getFacetInfoForAnomaly(anomaly);
    fetchAnomalyImages({
      facetAliasName: facetInfo.facetAliasName,
      orderId: orderID,
      structureId: selectedStructureId,
      lat: latlon.lat,
      long: latlon.lon,
    });
    setSelectedAnomalyForDialog(anomaly);
    setfacetImageGalleryDialogOpen(true);
    setIsAnomalyImages(true);
  };

  const closeAnomalyImagesDialog = () => {
    setfacetImageGalleryDialogOpen(false);
    setIsAnomalyImages(false);
  };

  // Implementation of includeFacetAndDamageInReport function
  const includeFacetAndDamageInReport = () => {
    const selectedAnomalyFacetInfo = getSelectedAnomalyFacetInfo();
    const selectedAnomalyIsUserAdded = isSelectedAnomalyUserAdded();
    const selectedAnomalyIsConfirmed = isSelectedAnomalyConfirmed();

    if (selectedAnomalyFacetInfo) {
      const {
        includeInReport, facetId, decision, facetAliasName,
      } = selectedAnomalyFacetInfo;

      // Include facet in report if not already included
      if (!includeInReport) {
        saveFacetReportInclusion({
          orderId: orderID,
          facetId,
          data: {
            decision,
            facetAliasName,
            roofId,
            includeInReport: true,
          },
        });
      }
    }

    // Include anomaly in report if it's user added or confirmed
    if (selectedAnomalyIsUserAdded || (!selectedAnomalyIsUserAdded && selectedAnomalyIsConfirmed)) {
      toggleAnomalyIncludeInReport({
        orderID,
        anomalyID: selectedAnomalyId,
        data: {
          includeInReport: true,
        },
      });
    }
  };

  // Confirmation dialog handlers
  const handleDeleteUserAnomalyOpen = (anomalyId) => {
    setDeleteAnomalyId(anomalyId);
    setDeleteUserAnomalyDialogOpen(true);
  };

  const handleDeleteUserAnomalyClose = () => {
    setDeleteUserAnomalyDialogOpen(false);
    setDeleteAnomalyId(UNSET_ANOMALY_ID);
  };

  const handleDeleteUserAnomalyConfirm = () => {
    if (!orderID || !deleteAnomalyId || deleteAnomalyId === UNSET_ANOMALY_ID) return;

    deleteUserAnomaly({
      orderId: orderID,
      anomalyId: deleteAnomalyId,
    });

    setDeleteUserAnomalyDialogOpen(false);
    setDeleteAnomalyId(UNSET_ANOMALY_ID);
  };

  // Extracted function for handling positioning after animation completes
  const handlePostAnimationPositioning = (anomalyId) => {
    // Get pixel position from the map viewer after the animation
    const pixelPosition = wireframeMapViewerRef.current.getAnomalyPixelPosition(anomalyId);

    if (!pixelPosition) {
      /* noop: debug disabled */
      return;
    }

    // Calculate smart positioning using utility function
    const smartPosition = calculateSmartPosition(
      pixelPosition,
      wrapperRef,
      annotationDescPanelConstants,
      isCompleteVisibleH,
      isCompleteVisibleV,
    );

    // Clamp to container boundaries using utility function
    const finalPosition = clampPositionToContainer(
      smartPosition,
      wrapperRef.current,
      { panelWidth: smartPosition.panelWidth, panelHeight: smartPosition.panelHeight },
    );

    // Create position object and update state
    const panelPosition = createPanelPosition(finalPosition.top, finalPosition.left);

    setAnomalyDescriptionPosition(panelPosition);
    setHoveredAnomalyId(anomalyId);
    setSelectedAnomalyId(anomalyId);
    setShowAnomalyDescription(true);
  };

  // Function to handle anomaly description opening with pixel position
  const handleOpenAnomalyDescription = (anomalyId) => {
    // Validate refs first using utility function
    if (!validateRefs(wireframeMapViewerRef, wrapperRef)) {
      /* noop: debug disabled */
      return;
    }

    // Find the target anomaly using utility function
    const targetAnomaly = findTargetAnomaly(testSquareAnomalies, anomalyId);

    // Validate anomaly geometry using utility function
    if (!validateAnomalyGeometry(targetAnomaly)) {
      /* noop: debug disabled */
      return;
    }

    // Clear previous anomaly notes first to avoid showing stale data
    clearAnomalyNotes();

    // Fetch anomaly notes first (like in Adjuster.js)
    fetchAnomalyNoteDetails({
      orderId: orderID,
      anomalyId,
    });

    // Extract and validate coordinates using utility function
    const coordinateResult = extractCoordinates(targetAnomaly);
    if (!coordinateResult.valid) {
      /* noop: debug disabled */
      return;
    }

    const { lon, lat } = coordinateResult;

    // Calculate zoom level using utility function
    const anomalyZoomLevel = calculateAnomalyZoomLevel(TESTSQUARE_ZOOM_LEVEL_ON_SELECTION);

    // Create easeTo configuration using utility function
    const easeToConfig = createEaseToConfig(lon, lat, anomalyZoomLevel, 1000);

    // Execute the pan/zoom animation
    wireframeMapViewerRef.current.easeTo(easeToConfig);

    // Wait for the animation to complete before showing the description panel
    setTimeout(() => {
      handlePostAnimationPositioning(anomalyId);
    }, 1200); // Wait slightly longer than the animation duration to ensure it's complete
  };

  // Anomaly decision handler for system-detected anomalies
  const confirmOrRemoveAnomalyDecision = (isRemove, anomalyId) => {
    const decision = isRemove ? 'REMOVED' : 'CONFIRMED';
    const anomalyLocal = selectedFacetForDialog.anomalyList.find((a) => a.anomalyId === anomalyId);
    anomalyLocal.decision = isRemove ? ANOMALY_DECISIONS.REMOVED : ANOMALY_DECISIONS.CONFIRMED;
    anomalyLocal.includeInReport = isEntitled(entitlements, FEATURE_ENTITLEMENTS.MANAGE_REPORTS) ? !isRemove : anomalyLocal.includeInReport;
    setAnomalyDecision(anomalyLocal);
    saveAnomalyDecision({ orderId: orderID, anomalyData: anomalyLocal, orgDecision: decision });
  };

  // Helper functions for each anomaly card
  const getMappedAnomalyTypeForAnomaly = (anomaly) => {
    if (anomaly && anomaly.properties) {
      if (anomaly.properties.type === 'crease') {
        return 'wind';
      }
      return anomaly.properties.type;
    }
    return '';
  };

  const createAnomalyHandlers = (anomaly) => ({
    onTypeChange: ({ target: { value: anomalyType } }) => {
      updateAnomalyType({
        orderID,
        anomalyID: anomaly.properties.anomalyId,
        data: {
          properties: {
            type: anomalyType,
            confidence: '1.0',
          },
        },
      });
    },
    handleDeleteUserAnomalyOpen: () => {
      // For user-added anomalies, show confirmation dialog
      if (anomaly.properties.isUserAdded) {
        handleDeleteUserAnomalyOpen(anomaly.properties.anomalyId);
      } else {
        // For system-detected anomalies, call directly
        confirmOrRemoveAnomalyDecision(true, anomaly.properties.anomalyId);
      }
    },
    onIncludeInReport: () => {
      toggleAnomalyIncludeInReport({
        orderID,
        anomalyID: anomaly.properties.anomalyId,
        data: {
          includeInReport: !anomaly.properties.includeInReport,
        },
      });
    },
    onExpandClick: () => openAnomalyImagesDialog(anomaly),
    getMappedAnomalyType: () => getMappedAnomalyTypeForAnomaly(anomaly),
  });

  const getAllAnomalyCardElements = () => {
    const container = anomalyCardsContainerRef.current;
    if (!container) return [];
    return Array.from(container.querySelectorAll('[data-anomaly-id]'));
  };

  const getVisibleAnomalyIdsWithBuffer = (bufferSize = 3) => {
    const container = anomalyCardsContainerRef.current;
    if (!container) return [];
    const cards = getAllAnomalyCardElements();
    const containerRect = container.getBoundingClientRect();

    // Find visible indexes
    const visibleIndexes = cards
      .map((card, idx) => {
        const cardRect = card.getBoundingClientRect();
        if (
          cardRect.bottom > containerRect.top
        && cardRect.top < containerRect.bottom
        ) {
          return idx;
        }
        return null;
      })
      .filter((idx) => idx !== null);

    if (visibleIndexes.length === 0) return [];

    const firstVisible = visibleIndexes[0];
    const lastVisible = visibleIndexes[visibleIndexes.length - 1];

    // Calculate buffered indexes
    const start = Math.max(0, firstVisible - bufferSize);
    const end = Math.min(cards.length - 1, lastVisible + bufferSize);

    const bufferedIds = cards
      .slice(start, end + 1)
      .map((card) => card.getAttribute('data-anomaly-id'));

    return bufferedIds;
  };
  useEffect(() => {
    const currentAnomalyIds = testSquareAnomalies.map((anomaly) => anomaly.properties.anomalyId);
    const previousAnomalyIds = previousAnomaliesRef.current;
    // Find newly added anomalies (present in current but not in previous)
    const newlyAddedAnomalyIds = currentAnomalyIds.filter((id) => !previousAnomalyIds.includes(id));
    if (newlyAddedAnomalyIds.length > 0) {
      setNewlyAddedAnomalyIds(newlyAddedAnomalyIds);
    }
    // Update the ref with current anomaly IDs for next comparison
    previousAnomaliesRef.current = currentAnomalyIds;
  }, [showRemovedAnomalies]);
  const getMissingSnapshotIds = (anomalyIds) => anomalyIds.filter((id) => !anomalySnapshots[id]);

  // On initial load and on scroll, always check all buffered IDs
  useEffect(() => {
    const fetchMissingAnomalies = () => {
      const bufferedIds = getVisibleAnomalyIdsWithBuffer(3); // 3 before, 3 after
      let missingIds = getMissingSnapshotIds(bufferedIds);
      if (showRemovedAnomalies) {
        missingIds = union(missingIds, newlyAddedAnomalyIdstate);
      }

      if (missingIds.length > 0) {
        fetchAnomalySnapshots({ anomalyIds: missingIds, orderId: orderID });
      }
    };
    fetchMissingAnomalies();

    const container = anomalyCardsContainerRef.current;
    if (!container) return;
    let scrollTimeout = null;
    const onScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(fetchMissingAnomalies, 500);
    };
    container.addEventListener('scroll', onScroll);

    // eslint-disable-next-line consistent-return
    return () => {
      container.removeEventListener('scroll', onScroll);
      clearTimeout(scrollTimeout);
    };
  }, [anomalyCardsContainerRef.current, showRemovedAnomalies]);

  useEffect(() => {
    const onResize = () => {
    // ...same call as above
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Track pointer position globally so we can infer hovered card immediately after menus close
  useEffect(() => {
    const handlePointerMove = (event) => {
      lastMousePositionRef.current = { x: event.clientX, y: event.clientY };
    };
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    return () => window.removeEventListener('pointermove', handlePointerMove);
  }, []);

  // After any global click (e.g., clicking a different select to close the current one),
  // evaluate which card is currently under the pointer and set hover accordingly
  useEffect(() => {
    const resolveFromPoint = () => {
      const attemptResolve = (attempt) => {
        const { x, y } = lastMousePositionRef.current;
        const topElement = document.elementFromPoint(x, y);
        if (!topElement) {
          if (attempt < 3) setTimeout(() => attemptResolve(attempt + 1), 50);
          return;
        }
        const containerEl = anomalyCardsContainerRef.current;
        if (!containerEl) return;
        // 1) If menu/paper from select is hovered, it carries data-ev-select-anomaly-id attrs
        const selectAnomalyIdAttr = topElement.closest && topElement.closest('[data-ev-select-anomaly-id]');
        if (selectAnomalyIdAttr) {
          const anomalyId = selectAnomalyIdAttr.getAttribute('data-ev-select-anomaly-id');
          if (anomalyId) {
            setHoveredAnomalyId(anomalyId);
            return;
          }
        }
        // 2) Fallback to the card under pointer
        const cardEl = topElement.closest ? topElement.closest('[data-anomaly-id]') : null;
        if (cardEl && containerEl.contains(cardEl)) {
          const anomalyId = cardEl.getAttribute('data-anomaly-id');
          if (anomalyId) setHoveredAnomalyId(anomalyId);
          return;
        }
        if (attempt < 3) setTimeout(() => attemptResolve(attempt + 1), 50);
      };
      // Defer to allow menus/backdrops to unmount
      setTimeout(() => attemptResolve(0), 0);
    };

    const handleGlobalClickCapture = () => {
      resolveFromPoint();
    };
    const handleGlobalPointerDownCapture = () => {
      resolveFromPoint();
    };
    const handleGlobalPointerOverCapture = (event) => {
      // Highlight immediately while moving over a select/menu
      const { target } = event;
      const containerEl = anomalyCardsContainerRef.current;
      if (!containerEl || !target) return;
      const selectAnomalyIdAttr = target.closest ? target.closest('[data-ev-select-anomaly-id]') : null;
      if (selectAnomalyIdAttr) {
        const anomalyId = selectAnomalyIdAttr.getAttribute('data-ev-select-anomaly-id');
        if (anomalyId) {
          setHoveredAnomalyId(anomalyId);
          return;
        }
      }
      const cardEl = target.closest ? target.closest('[data-anomaly-id]') : null;
      if (cardEl && containerEl.contains(cardEl)) {
        const anomalyId = cardEl.getAttribute('data-anomaly-id');
        if (anomalyId) setHoveredAnomalyId(anomalyId);
      }
    };
    document.addEventListener('click', handleGlobalClickCapture, true);
    document.addEventListener('pointerdown', handleGlobalPointerDownCapture, true);
    document.addEventListener('pointerover', handleGlobalPointerOverCapture, true);
    return () => {
      document.removeEventListener('click', handleGlobalClickCapture, true);
      document.removeEventListener('pointerdown', handleGlobalPointerDownCapture, true);
      document.removeEventListener('pointerover', handleGlobalPointerOverCapture, true);
    };
  }, [anomalyCardsContainerRef.current]);

  return (
    <>
      <EVDialog
        fullWidth
        maxWidth="xl"
        open={open}
        onClose={onClose}
      >
        <EVIconButton
          aria-label="close"
          onClick={onClose}
          className={classes.closeIconButton}
        >
          <EVClear />
        </EVIconButton>
        <EVDialogContent className={classes.panelContent} ref={wrapperRef}>
          <EVBox display="flex">
            <EVBox
              width="50%"
              height="80vh"
            >
              <TestSquareWireFrameMapViewer
                testSquareData={testSquareData}
                facetPolygons={facetPolygons}
                anomalies={anomalies}
                hoveredAnomalyId={hoveredAnomalyId}
                onAnomalyHover={({ anomalyId, eventType }) => {
                  if (anomalyId && eventType) {
                    setHoveredAnomalyId(anomalyId);
                    if (eventType === 'click') {
                      scrollToAnomalyCard(anomalyId);
                    }
                  }
                }}
                ref={wireframeMapViewerRef}
              />
            </EVBox>
            <EVBox width="50%">
              <EVBox display="flex" alignItems="center" style={{ marginBottom: '5px', paddingLeft: '16px' }}>
                <EVCheckbox
                  checked={showRemovedAnomalies}
                  onChange={() => toggleRemovedAnomalies(!showRemovedAnomalies)}
                  color="primary"
                />
                <span style={{ marginLeft: '8px', fontSize: '14px' }}>
                  {t('adjuster.testsquareAnomalyDecisionsPanel.showAllRemoved')}
                </span>
              </EVBox>
              <div className={classes.anomalyCardsGrid} ref={anomalyCardsContainerRef}>
                {
                  testSquareAnomalies.map((anomaly) => (
                    <AnomalyCardItem
                      key={anomaly.properties.anomalyId}
                      data-anomaly-id={anomaly.properties.anomalyId}
                      style={{}} // Add style prop for consistency
                      highlight={hoveredAnomalyId === anomaly.properties.anomalyId}
                      anomaly={anomaly}
                      handlers={createAnomalyHandlers(anomaly)}
                      t={t}
                      confirmOrRemoveAnomalyDecision={confirmOrRemoveAnomalyDecision}
                      handleOpenAnomalyDescription={handleOpenAnomalyDescription}
                      entitlements={entitlements}
                      setHoveredAnomalyId={setHoveredAnomalyId}
                      classes={classes}
                    />
                  ))
                }
              </div>
            </EVBox>
          </EVBox>
          {showAnomalyDescription && (
            <TestSquareAnomalyNotesPanel
              coordinates={anomalyDescriptionPosition}
              selectedAnomalyId={selectedAnomalyId}
              setShowAnomalyDescription={setShowAnomalyDescription}
              setSelectedAnomalyId={setSelectedAnomalyId}
              includeFacetAndDamageInReport={includeFacetAndDamageInReport}
            />
          )}
        </EVDialogContent>
      </EVDialog>
      <EVDialog open={deleteUserAnomalyDialogOpen} onClose={handleDeleteUserAnomalyClose}>
        <EVDialogContent>
          <EVDialogContentText>{t('workflowpanel.userAnomalyDeletConfirm')}</EVDialogContentText>
        </EVDialogContent>
        <EVDialogActions>
          <EVButton onClick={handleDeleteUserAnomalyClose}>{t('cancel')}</EVButton>
          <EVButton onClick={handleDeleteUserAnomalyConfirm} color="primary" autoFocus>
            {t('delete')}
          </EVButton>
        </EVDialogActions>
      </EVDialog>
      <ErrorBoundary
        componentName="WorkflowPanel: FacetImagesDialog"
        alertMsg={t('workFlowPanel.softMetalDialogCrash')}
      >
        {selectedFacetForDialog && isAnomalyImages && (
          isFacetRawImageryWorkflowEnabled ? (
            <FacetImagesRawImageryDialog
              open={facetImageGalleryDialogOpen}
              handleClose={closeAnomalyImagesDialog}
              facetAliasName={selectedFacetForDialog.facetAliasName}
              facetId={selectedFacetForDialog.facetId}
              facetDecision={selectedFacetForDialog.decision}
              facetIncludeInReport={selectedFacetForDialog.includeInReport}
              imageUrn={selectedAnomalyForDialog.properties.imageUrn}
            />
          ) : (
            <FacetImagesDialog
              open={facetImageGalleryDialogOpen}
              handleClose={closeAnomalyImagesDialog}
              facetAliasName={selectedFacetForDialog.facetAliasName}
              isAnomalyImages={isAnomalyImages}
              facetId={selectedFacetForDialog.facetId}
              facetDecision={selectedFacetForDialog.decision}
              facetIncludeInReport={selectedFacetForDialog.includeInReport}
            />
          )
        )}
      </ErrorBoundary>
    </>
  );
};

TestsquareAnomalyDecisionsPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  testSquareData: PropTypes.shape({}),
  anomalies: PropTypes.arrayOf(PropTypes.shape({
    uuid: PropTypes.string,
    assetType: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      geometries: PropTypes.shape({}),
      properties: PropTypes.shape({}),
      style: PropTypes.shape({}),
      georeference: PropTypes.string,
    })),
  })),
  showRemovedAnomalies: PropTypes.bool.isRequired,
  toggleRemovedAnomalies: PropTypes.func.isRequired,
};

TestsquareAnomalyDecisionsPanel.defaultProps = {
  testSquareData: {},
  anomalies: [],
};

export default TestsquareAnomalyDecisionsPanel;
